import { NavLink, Outlet } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { api } from '../api/cliente.js';
import { Marca } from '../componentes/Marca.js';
import { useSessao } from '../auth/SessaoContexto.js';

/**
 * Casca do portal da operadora.
 *
 * Sem sidebar, de propósito. O cliente tem três telas e visita o portal para
 * resolver uma coisa pontual — normalmente trocar um reverso. Uma navegação
 * lateral com quatorze itens vazios, ou três perdidos numa coluna larga,
 * faria o portal parecer uma versão quebrada do painel em vez de uma
 * ferramenta própria.
 */

const ABAS = [
  { para: '/', rotulo: 'Meus IPs' },
  { para: '/solicitacoes', rotulo: 'Solicitações' },
  { para: '/delegacao', rotulo: 'Delegação' },
];

export function PortalShell() {
  const { usuario, sair } = useSessao();

  const { data } = useQuery({
    queryKey: ['portal', 'resumo'],
    queryFn: () =>
      api.get<{ operadora: { nome: string } | null; totais: { pendentes: number; dominios: number } }>(
        '/portal/resumo',
      ),
  });

  // A aba de Domínios só entra quando a operadora tem algum domínio associado —
  // caso contrário o cliente veria uma seção que não usa.
  const abas =
    (data?.totais.dominios ?? 0) > 0 ? [...ABAS, { para: '/dominios', rotulo: 'Domínios' }] : ABAS;

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-20 border-b border-borda bg-superficie">
        <div className="mx-auto flex h-topbar max-w-[1200px] items-center gap-4 px-4 lg:px-6">
          <Marca tamanho="pequeno" />

          <div className="hidden min-w-0 border-l border-borda pl-4 sm:block">
            <p className="truncate text-[13px] font-bold leading-tight">Portal do cliente</p>
            <p className="truncate text-[12px] text-texto-suave">
              {data?.operadora?.nome ?? '…'}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <NavLink
              to="/perfil"
              className={({ isActive }) =>
                `hidden rounded-chip px-2 py-1 text-right transition-colors sm:block ${
                  isActive ? 'bg-borda-suave' : 'hover:bg-borda-suave'
                }`
              }
              title="Meu perfil"
            >
              <span className="block text-[12.5px] font-bold leading-tight">{usuario?.nome}</span>
              <span className="block text-[11.5px] text-texto-suave">{usuario?.email}</span>
            </NavLink>
            <button
              onClick={() => void sair()}
              className="grid size-9 place-items-center rounded-chip text-texto-suave hover:bg-borda-suave hover:text-erro"
              aria-label="Sair"
              title="Sair"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        <nav className="mx-auto flex max-w-[1200px] gap-1 px-4 lg:px-6">
          {abas.map((a) => (
            <NavLink
              key={a.para}
              to={a.para}
              end={a.para === '/'}
              className={({ isActive }) =>
                `-mb-px border-b-2 px-3 py-2.5 text-[13.5px] font-bold transition-colors ${
                  isActive
                    ? 'border-marca text-marca'
                    : 'border-transparent text-texto-suave hover:text-texto'
                }`
              }
            >
              {a.rotulo}
              {a.para === '/solicitacoes' && (data?.totais.pendentes ?? 0) > 0 && (
                <span className="ml-1.5 rounded-pill bg-aviso-fundo px-1.5 py-px text-[11px] text-aviso">
                  {data?.totais.pendentes}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-[1200px] p-4 lg:p-6">
        <Outlet />
      </main>
    </div>
  );
}
